import apiGraph from "@/helpers/api/apiGraph";
import apiSharePoint from "@/helpers/api/apiSharePoint";
import Permission from "@/models/Documents/Permission";

export const permissions_state = {
  itemPermissionsMap: {} // { driveId: { itemId: { permissions-for-me }}},
};

export const permissions_actions = {
  /**
   * Load permissions for driveItem (only loads if not in store)
   * @param { object } $vuexStore Vuex
   * @param { string } driveId DriveId to load permissions for
   * @param { string } itemId ItemId to load permissions for
   * @returns { object } Permission object
   */

  async loadDocumentPermissions(driveId, itemId) {
    const calcItemId = this.calculateRootOrItemId(driveId, itemId);

    if (!this.myItemPermission(driveId, calcItemId)) {
      await this.reloadDocumentPermissions(driveId, itemId);
    }

    return this.myItemPermission(driveId, calcItemId);
  },

  /**
   * Reload permissions for driveItem
   * @param { string } driveId DriveId to load permissions for
   * @param { string } itemId ItemId to load permissions for
   * @returns { object } Permission object
   */
  async reloadDocumentPermissions(driveId, itemId) {
    if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
      const response = await apiGraph.get(`/drives/${driveId}/items/${itemId}/permissions`).catch(() => {
        const map = Object.assign({}, this.itemPermissionsMap);
        if (!map[driveId]) {
          map[driveId] = {};
        }

        map[driveId][itemId] = new Permission(null, null, null, Permission.nctPermission.None);
        this.itemPermissionsMap = map;
      });

      if (response && response.data) {
        const map = Object.assign({}, this.itemPermissionsMap);
        if (!map[driveId]) {
          map[driveId] = {};
        }

        map[driveId][itemId] = calculatePermissionsFromSharePointPermissionObject(response.data);
        this.itemPermissionsMap = map;
      }
    } else {
      let driveItem = await this.loadDriveItem(driveId, itemId);

      if (driveItem && driveItem.webUrl && driveItem.webUrl.length > 0) {
        const regexSite = /^((https?:\/\/?[^:/]*\/sites\/[^:/]*\/)|(https?:\/\/?[^:/]*)).*$/g; // https://regex101.com/r/rEiXyO/6

        const regexSiteAndFiles = /(\/\/[a-zA-Z0-9.]+)([/sites]?\/[^?]+)/g; // https://regex101.com/r/UVox6L/4

        // https://stackoverflow.com/questions/22264002/how-to-get-permission-levels-for-files-folders-documents-in-sharepoint-using-res

        let result;
        if ((driveItem.folder || driveItem.isFolder) && !driveItem.root) {
          const urlSplit = regexSite.exec(driveItem.webUrl);
          const urlSplitFiles = regexSiteAndFiles.exec(driveItem.webUrl);

          result = await apiSharePoint.get(
            `${urlSplit[1]}/_api/web/getFolderByServerRelativeUrl('${urlSplitFiles[2]}')/ListItemAllFields/EffectiveBasePermissions`
          );
        } else if (driveItem.root) {
          const urlSplit = regexSite.exec(driveItem.webUrl);
          const urlSplitFiles = regexSiteAndFiles.exec(driveItem.webUrl);
          const res = await apiSharePoint.get(
            `${urlSplit[1]}/_api/web/getFolderByServerRelativeUrl('${urlSplitFiles[2]}')/Properties`
          );
          if (res && res.data && res.data.vti_x005f_listtitle) {
            result = await apiSharePoint.get(
              `${urlSplit[1]}/_api/web/getListByTitle('${res.data.vti_x005f_listtitle}')/EffectiveBasePermissions`
            );
          }
        } else {
          const parentDriveItem = await this.loadDriveItem(driveId, driveItem.parentReference.id);
          const urlSplit = regexSite.exec(parentDriveItem.webUrl);
          const urlSplitFiles = regexSiteAndFiles.exec(parentDriveItem.webUrl);
          result = await apiSharePoint.get(
            `${urlSplit[1]}/_api/web/getFileByServerRelativeUrl('${urlSplitFiles[2]}/${driveItem.name}')/ListItemAllFields/EffectiveBasePermissions`
          );
        }

        if (result && result.data) {
          const map = Object.assign({}, this.itemPermissionsMap);
          if (!map[driveId]) {
            map[driveId] = {};
          }

          map[driveId][itemId] = calculatePermissionsFromSharePointPermissionObject(result.data);
          this.itemPermissionsMap = map;
        }
      } else {
        const map = Object.assign({}, this.itemPermissionsMap);
        if (!map[driveId]) {
          map[driveId] = {};
        }
        map[driveId][itemId] = new Permission();
        this.itemPermissionsMap = map;
      }
    }
    return this.itemPermissionsMap[driveId][itemId];
  }
};

export const permissions_getters = {
  myItemPermission(state) {
    return (driveId, itemId) => {
      const realDriveId = this.calculateRootOrDriveId(driveId);
      const realItemId = itemId;

      if (state.itemPermissionsMap[realDriveId]) {
        if (state.itemPermissionsMap[realDriveId][realItemId]) {
          return state.itemPermissionsMap[realDriveId][realItemId];
        }
      }
      return null;
    };
  }
};

/**
 * Calculates permission to from SharePoint permission object
 * @param { object } permissionObject Permission object from SharePoint EffectiveBasePermissions result
 * @returns { object } Permission object
 */
function calculatePermissionsFromSharePointPermissionObject(permissionObject) {
  if ((permissionObject.Low & (1 << (4 - 1))) != 0) {
    return new Permission(null, null, null, Permission.nctPermission.OWNER);
  } else if ((permissionObject.Low & (1 << (3 - 1))) != 0) {
    return new Permission(null, null, null, Permission.nctPermission.WRITE);
  } else if ((permissionObject.Low & (1 << (1 - 1))) != 0) {
    return new Permission(null, null, null, Permission.nctPermission.READ);
  } else {
    return new Permission(null, null, null, Permission.nctPermission.None);
  }
}
